import { motion } from "framer-motion";
import { Lightbulb, Pencil, PenTool, Layers, Handshake, Package, Map, Rocket } from "lucide-react";
import { IMG } from "@/lib/images";

const STAGES = [
  { icon: Lightbulb, name: "Idea", status: "complete" },
  { icon: Pencil, name: "Sketches", status: "complete" },
  { icon: PenTool, name: "Design", status: "complete" },
  { icon: Layers, name: "Materials", status: "active" },
  { icon: Handshake, name: "Factory Partner", status: "active" },
  { icon: Package, name: "Prototype V1", status: "next" },
  { icon: Map, name: "Road Testing", status: "future" },
  { icon: Rocket, name: "Launch", status: "future" },
];

const STATUS_LABEL = {
  complete: "Complete",
  active: "In Progress",
  next: "Up Next",
  future: "On The Horizon",
};

export const Timeline = () => {
  return (
    <section id="journey" data-testid="timeline-section" className="relative py-28 md:py-36 bg-[#0A0A0C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="section-mark" />
            <span className="overline">Building ROADRIG · The Road So Far</span>
          </div>
          <h2 className="h-section text-white text-4xl sm:text-5xl lg:text-6xl">
            Idea to highway. <br />
            <span className="text-white/40">Eight stages. One bag.</span>
          </h2>
        </div>

        {/* Desktop horizontal timeline */}
        <div className="hidden md:block relative">
          <div className="absolute top-9 left-0 right-0 h-px bg-white/10" />
          <div className="absolute top-9 left-0 h-px bg-[#F47A20]" style={{ width: "40%" }} />
          <div className="grid grid-cols-8 gap-2">
            {STAGES.map((s, i) => {
              const isActive = s.status === "active";
              const isComplete = s.status === "complete";
              const isNext = s.status === "next";
              return (
                <motion.div
                  key={s.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="relative flex flex-col items-center text-center"
                  data-testid={`timeline-stage-${i}`}
                >
                  <div
                    className={`relative z-10 w-[72px] h-[72px] grid place-items-center rounded-full border-2 transition-all ${
                      isComplete
                        ? "bg-[#F47A20] border-[#F47A20] text-white"
                        : isActive
                        ? "bg-[#141417] border-[#F47A20] text-[#F47A20] glow-orange"
                        : isNext
                        ? "bg-[#141417] border-white/30 text-white"
                        : "bg-[#141417] border-white/10 text-white/30"
                    }`}
                  >
                    <s.icon size={26} strokeWidth={1.6} />
                  </div>
                  <div className="mt-5 serial text-[10px] text-white/30">0{i + 1}</div>
                  <div className="mt-1 h-display text-base text-white leading-tight">{s.name}</div>
                  <div className={`mt-1 serial text-[9px] ${isActive ? "text-[#F47A20]" : isComplete ? "text-white/70" : "text-white/30"}`}>
                    {STATUS_LABEL[s.status]}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mobile vertical */}
        <div className="md:hidden space-y-4">
          {STAGES.map((s, i) => {
            const isActive = s.status === "active";
            const isComplete = s.status === "complete";
            return (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex items-center gap-4 p-4 bg-[#141417] border border-white/5 rounded-sm"
                data-testid={`timeline-stage-m-${i}`}
              >
                <div
                  className={`w-14 h-14 grid place-items-center rounded-full ${
                    isComplete ? "bg-[#F47A20] text-white" : isActive ? "bg-[#1A1A1D] border-2 border-[#F47A20] text-[#F47A20]" : "bg-[#1A1A1D] border border-white/10 text-white/40"
                  }`}
                >
                  <s.icon size={22} strokeWidth={1.6} />
                </div>
                <div>
                  <div className="serial text-[10px] text-white/30">0{i + 1}</div>
                  <div className="h-display text-lg text-white">{s.name}</div>
                  <div className={`serial text-[9px] ${isActive ? "text-[#F47A20]" : isComplete ? "text-white/70" : "text-white/30"}`}>
                    {STATUS_LABEL[s.status]}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Featured journey panel — real founder photography */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
          data-testid="timeline-journey-poster"
        >
          <div className="lg:col-span-5">
            <div className="serial text-[10px] text-[#F47A20] mb-4">FOUNDER MANIFESTO · ONE RIDER, ONE BAG</div>
            <h3 className="h-display text-3xl sm:text-4xl text-white mb-4 leading-tight">
              More than a rider bag. <br />
              <span className="text-white/40">A purpose.</span>
            </h3>
            <p className="text-white/65 leading-relaxed">
              From the first idea to the prototype, every step has been taken with one goal in mind: to build the rider bag we&apos;ve always wanted. Behind the scenes. Real moments. Real progress.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["The Idea", "The Design", "The Process", "The Prototype", "The Road Ahead"].map((t) => (
                <span key={t} className="serial text-[10px] text-white/70 px-3 py-1.5 border border-white/10 bg-[#141417]">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-3">
              <div className="aspect-[3/4] overflow-hidden rounded-sm border border-white/10">
                <img
                  src={IMG.FRUSTRATION_JOHN}
                  alt="John, ROADRIG founder"
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: "50% 30%" }}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-sm border border-white/10 mt-10">
                <img
                  src={IMG.RIDE_COAST}
                  alt="John riding the Road Glide along the coast"
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: "50% 45%" }}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
